import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Nav, NavItem, NavLink, Spinner } from "reactstrap";
import classnames from "classnames";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import useEarnedMediaByAccount from "../query/hooks/useEarnedMediaByAccount";

const EarnedMediaByAccountGraph = ({ selectedCurrency }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(1);
  const { data, isLoading, isRefetching, isSuccess } = useEarnedMediaByAccount();

  const activeTabData = useMemo(
    () => JSON.parse(JSON.stringify(data?.[activeTab] ?? [])),
    [data, activeTab]
  );

  const chartOptions = (graph) => {
    if (!graph.data) {
      return;
    }
    const series = graph.data.series.map((s) => {
      const platformIcon = s.platform === 'instagram'
        ? '<i class="mdi mdi-instagram" style="color: #E1306C;"></i>'
        : '<i class="mdi mdi-facebook" style="color: #1877F2;"></i>';

      return {
        ...s,
        name: `${s.name} ${platformIcon}`,
        data: s.data.map((d) => {
          const value = typeof d === "number" && !isNaN(d) ? d : 0;
          return value;
        }),
      };
    });
    return {
      credits: { enabled: false },
      chart: {
        type: "spline",
        backgroundColor: "transparent",
      },
      legend: {
        useHTML: true,
        itemStyle: {
          color: "var(--text-primary)",
        },
      },
      plotOptions: {
        spline: {
          showInLegend: true,
        },
      },
      title: {
        text: "",
      },
      yAxis: {
        title: {
          text: graph.data.yTitle,
          style: {
            color: "var(--text-primary)",
          },
        },
        labels: {
          style: {
            color: "var(--text-primary)",
          },
        },
      },
      xAxis: {
        categories: graph.data.labels,
        labels: {
          step: 0,
          style: {
            color: "var(--text-primary)",
          },
        },
        title: {
          text: graph.data.xTitle,
          style: {
            color: "var(--text-primary)",
          },
        },
      },
      series,
    };
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const renderTab = (graph, index) => {
    return (
      <NavItem key={index}>
        <NavLink
          href="#"
          className={classnames({ active: activeTab === index })}
          onClick={() => toggleTab(index)}
        >
          {graph.source}
        </NavLink>
      </NavItem>
    );
  };

  // Show placeholder when no data
  if (isSuccess && (!data || data.length === 0)) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "200px" }}
      >
        <p className="text-muted mb-0">{t("no_earned_media_data")}</p>
      </div>
    );
  }

  return (
    <>
      {(isLoading || isRefetching) && (
        <Spinner
          color="primary"
          size="sm"
          style={{ position: "absolute", right: 20 }}
        />
      )}
      {isSuccess && (
        <>
          <div
            style={{ display: "flex", alignItems: "center" }}
            className="my-4 flex wrap flex-wrap"
          >
            <div style={{ flex: 1 }}></div>
            <Nav pills className="navtab-bg center-pills">
              {Array.isArray(data) &&
                data.map((graph, index) => renderTab(graph, index))}
            </Nav>
            <div style={{ flex: 1 }}></div>
          </div>
          {activeTabData && data?.[activeTab] && (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions(activeTabData)}
            />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  date: state.date,
  tenant: state.auth.tenant,
  selectedCurrency: state.currency.selectedCurrency,
});

export default connect(mapStateToProps)(EarnedMediaByAccountGraph);
